// Drawing App - Symmetry Transformations

import { Point, SymmetryMode } from './types';

export class SymmetryEngine {
  private mode: SymmetryMode = 'none';

  setMode(mode: SymmetryMode): void {
    this.mode = mode;
  }

  getMode(): SymmetryMode {
    return this.mode;
  }

  transform(point: Point, canvasWidth: number, canvasHeight: number): Point[] {
    if (this.mode === 'none') return [point];

    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const relX = point.x - centerX;
    const relY = point.y - centerY;

    switch (this.mode) {
      case '2-axis':
        return this.axis2(point, relX, relY, centerX, centerY);
      case '4-axis':
        return this.axis4(point, relX, relY, centerX, centerY);
      case '6-axis':
        return this.axisN(point, relX, relY, centerX, centerY, 6);
      case '8-axis':
        return this.axisN(point, relX, relY, centerX, centerY, 8);
      case 'radial':
        return this.axisN(point, relX, relY, centerX, centerY, 12);
      default:
        return [point];
    }
  }

  private axis2(point: Point, relX: number, relY: number, cx: number, cy: number): Point[] {
    return [
      point,
      { ...point, x: cx - relX, y: point.y }, // Horizontal mirror
    ];
  }

  private axis4(point: Point, relX: number, relY: number, cx: number, cy: number): Point[] {
    return [
      point,
      { ...point, x: cx - relX, y: point.y },   // Horizontal mirror
      { ...point, x: point.x, y: cy - relY },    // Vertical mirror
      { ...point, x: cx - relX, y: cy - relY },  // Both axes mirror
    ];
  }

  private axisN(point: Point, relX: number, relY: number, cx: number, cy: number, n: number): Point[] {
    const points: Point[] = [];
    const angleStep = (Math.PI * 2) / n;
    const baseAngle = Math.atan2(relY, relX);
    const dist = Math.hypot(relX, relY);

    for (let i = 0; i < n; i++) {
      const angle = baseAngle + angleStep * i;
      points.push({
        ...point,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist
      });
    }

    return points;
  }
}
